import { useState, useEffect } from "react";
import axios from "../axiosInstace";

const COMMON = process.env.REACT_APP_COMMON_API_URL; // http://localhost:8080/api/emp

/**
 * Fetches roles from /api/emp/roles/enabled
 * Returns { roles: [{ roleId, roleName }], rolesLoading, rolesError }
 */
export const useRoles = () => {
  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [rolesError, setRolesError] = useState(null);

  useEffect(() => {
    axios
      .get(`${COMMON}/roles/enabled`)
      .then((res) => setRoles(res.data.data || []))
      .catch((err) => {
        console.error("Failed to fetch roles:", err);
        setRolesError("Failed to load roles");
      })
      .finally(() => setRolesLoading(false));
  }, []);

  return { roles, rolesLoading, rolesError };
};

/**
 * Fetches departments from /api/emp/departments/list
 * Returns { departments: [{ departmentId, departmentName }], deptsLoading, deptsError }
 */
export const useDepartments = () => {
  const [departments, setDepartments] = useState([]);
  const [deptsLoading, setDeptsLoading] = useState(true);
  const [deptsError, setDeptsError] = useState(null);

  useEffect(() => {
    axios
      .get(`${COMMON}/departments/list`)
      .then((res) => setDepartments(res.data.data || []))
      .catch((err) => {
        console.error("Failed to fetch departments:", err);
        setDeptsError("Failed to load departments");
      })
      .finally(() => setDeptsLoading(false));
  }, []);

  return { departments, deptsLoading, deptsError };
};

/**
 * Fetches both roles and departments together
 * Useful when a form needs both at the same time
 */
export const useRolesAndDepartments = () => {
  const { roles, rolesLoading, rolesError } = useRoles();
  const { departments, deptsLoading, deptsError } = useDepartments();

  return {
    roles,
    departments,
    loading: rolesLoading || deptsLoading,
    error: rolesError || deptsError,
  };
};

/**
 * Fetches approved vacancies for job post creation
 * Returns { vacancies, vacanciesLoading }
 */
export const useApprovedVacancies = () => {
  const [vacancies, setVacancies] = useState([]);
  const [vacanciesLoading, setVacanciesLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API_HR_URL}/recruitment/vacancies/approved`)
      .then((res) => setVacancies(res.data.data || []))
      .catch((err) => console.error("Failed to fetch approved vacancies:", err))
      .finally(() => setVacanciesLoading(false));
  }, []);

  return { vacancies, vacanciesLoading };
};

/**
 * Fetches all hiring flows
 * Returns { hiringFlows, hiringFlowsLoading }
 */
export const useHiringFlows = () => {
  const [hiringFlows, setHiringFlows] = useState([]);
  const [hiringFlowsLoading, setHiringFlowsLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API_HR_URL}/hiring-flow`)
      .then((res) => setHiringFlows(res.data.data || []))
      .catch((err) => console.error("Failed to fetch hiring flows:", err))
      .finally(() => setHiringFlowsLoading(false));
  }, []);

  return { hiringFlows, hiringFlowsLoading };
};
